import { memo } from 'react';
import { parseValue } from '../../format/instant';
import { useFormat } from '../useFormat';
import { DEFAULT_EMPTY_VALUE, type EmptyProps } from './shared';

/** Whether the two ends are told apart by their day or by their time of day. */
export type RangeStyle = 'date' | 'time';

export type DateTimeRangeProps = EmptyProps & {
  /** Defaults to `date`. */
  readonly style?: RangeStyle;
  /**
   * The earlier end. A string has to be ISO 8601; any other shape renders as `emptyValue`, as it
   * does for {@link DateTime}.
   */
  readonly from: Date | string | number | null | undefined;
  /** The later end, read the same way as `from`. */
  readonly to: Date | string | number | null | undefined;
  /** Appends the zone abbreviation once, at the end. Read by `time`. */
  readonly withZone?: boolean;
};

/**
 * Two instants as one phrase: `05/10/2026 – 05/31/2026`, `8:00 AM – 9:00 PM EDT`.
 *
 * The separator comes from the locale rather than from a dash written at the call site. A range
 * assembled by hand gets that wrong in every locale but the author's — ja-JP joins with a wave
 * dash and no spaces.
 *
 * Either end being absent makes the whole range absent: half a range reads as a fact rather than
 * as a gap, and a reader cannot tell which half is missing.
 *
 * A pair of days — `2026-01-15` to `2027-01-15` — is rendered as days, with no zone applied to
 * either end. Both have to name a day for that to hold: one day and one instant is a payload
 * disagreeing with itself, and guessing which end to trust would move a boundary silently.
 *
 * Asked for `time`, an end that names a day renders the empty value, as {@link DateTime} does with
 * the same value: the midnight in hand was supplied by the parser to stand in for a time the
 * payload never carried, and a clock is the one thing a day cannot be asked for.
 */
const DateTimeRange = memo(
  ({
    from,
    to,
    style = 'date',
    withZone,
    emptyValue = DEFAULT_EMPTY_VALUE,
  }: DateTimeRangeProps): JSX.Element => {
    const format = useFormat();
    const start = parseValue(from);
    const end = parseValue(to);

    if (start === undefined || end === undefined) return <>{emptyValue}</>;

    // A day has no time of day, so an end carrying one has no clock to put in a span. The midnight
    // the parser supplied stands in for a time nobody sent, and converting it would report an hour
    // the payload never named.
    if (style === 'time' && (start.day !== undefined || end.day !== undefined))
      return <>{emptyValue}</>;

    if (style === 'time')
      return <>{format.timeRange(start.date, end.date, { withZone })}</>;

    const plain = start.day !== undefined && end.day !== undefined;

    return (
      <>
        {plain
          ? format.plainDateRange(start.date, end.date)
          : format.dateRange(start.date, end.date)}
      </>
    );
  }
);

DateTimeRange.displayName = 'DateTimeRange';

export default DateTimeRange;
